// SettingsPage.jsx
import React, { useState, useEffect } from "react";
import { Container, Card, Row, Col, Form, Button, Alert, Badge } from "react-bootstrap";
import { useUser } from "../../contextProviders/userContext";
import AppShell from "../../components/AppShell";
import api, { storeAccessToken } from "../../api";
import { User, Lock, Bell, Shield, Save, CheckCircle } from "lucide-react";

const SettingsPage = () => {
  const { user, updateUser } = useUser();
  const [profile, setProfile] = useState({
    name: user?.name || "",
    surname: user?.surname || "",
    email: user?.email || "",
    studentNumber: user?.studentNumber || "",
    program: user?.degree || "",
    bio: user?.bio || "",
    organizationName: user?.organizationName || "",
  });
  const isOrganization = user?.role === "organization";

  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const [notifications, setNotifications] = useState({
    emailUpdates: user?.notifications?.emailUpdates ?? true,
    newAnnouncements: user?.notifications?.newAnnouncements ?? true,
    weeklyDigest: user?.notifications?.weeklyDigest ?? false,
    systemAlerts: user?.notifications?.systemAlerts ?? true,
  });

  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        surname: user.surname || "",
        email: user.email || "",
        studentNumber: user.studentNumber || "",
        program: user.degree || "",
        bio: user.bio || "",
        organizationName: user.organizationName || "",
      });
      setNotifications({
        emailUpdates: user.notifications?.emailUpdates ?? true,
        newAnnouncements: user.notifications?.newAnnouncements ?? true,
        weeklyDigest: user.notifications?.weeklyDigest ?? false,
        systemAlerts: user.notifications?.systemAlerts ?? true,
      });
    }
  }, [user]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback({ type: "", message: "" });
    try {
      const response = await api.put("/auth/profile", {
        name: profile.name,
        surname: profile.surname,
        email: profile.email,
        degree: profile.program,
        bio: profile.bio,
        ...(isOrganization ? { organizationName: profile.organizationName } : {}),
      });
      
      updateUser(response.data.user);
      setFeedback({
        type: "success",
        message: "Profile settings saved successfully!",
      });
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to update profile settings.",
      });
    } finally {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      setFeedback({
        type: "danger",
        message: "New passwords do not match!",
      });
      return;
    }
    setLoading(true);
    setFeedback({ type: "", message: "" });
    try {
      const response = await api.put("/auth/password", {
        currentPassword: passwords.current,
        newPassword: passwords.new,
      });
      if (response.data.token) {
        storeAccessToken(response.data.token);
      }
      setFeedback({
        type: "success",
        message: "Password updated successfully!",
      });
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to update password. Verify your current password.",
      });
    } finally {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNotificationToggle = async (key) => {
    const updatedNotifications = {
      ...notifications,
      [key]: !notifications[key],
    };
    setNotifications(updatedNotifications);
    try {
      const response = await api.put("/auth/notifications", updatedNotifications);
      updateUser(response.data.user);
    } catch (err) {
      console.error("Failed to save notification preferences:", err);
    }
  };

  // Modern Premium Styling
  const sectionCardStyle = {
    backgroundColor: "#fff",
    borderRadius: "16px",
    border: "1px solid #E5E7EB",
    boxShadow: "none",
  };

  return (
    <AppShell>
      <Container 
        fluid 
        className="student-responsive-page p-0"
      >

        {/* Header Section */}
        <Row className="align-items-center justify-content-between mt-3 text-start">
          <Col>
            <div className="d-flex flex-column">
              <h3 className="fw-bold mb-1" style={{ color: "#111827" }}>
                Account Settings
              </h3>
              <p className="mb-0 text-muted" style={{ fontSize: "0.95rem" }}>
                Manage your profile details, secure your account, and configure alerts.
              </p>
            </div>
          </Col>
        </Row>

        <hr style={{ color: "#E5E7EB", width: "100%", margin: "20px 0" }} />

        {feedback.message && (
          <Alert 
            variant={feedback.type} 
            dismissible 
            onClose={() => setFeedback({ type: "", message: "" })}
            className="mb-4 text-start d-flex align-items-center gap-2"
          >
            <CheckCircle size={18} />
            {feedback.message}
          </Alert>
        )}

        <Row className="g-4">
          {/* Profile Details Card */}
          <Col lg={8}>
            <Card style={sectionCardStyle} className="p-4 mb-4 text-start">
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="p-2.5 rounded-3" style={{ backgroundColor: "rgba(27, 58, 135, 0.08)", color: "#1B3A87" }}>
                  <User size={20} />
                </div>
                <h5 className="fw-bold mb-0" style={{ color: "#1B3A87" }}>Personal Profile Details</h5>
              </div>

              <Form onSubmit={handleProfileSave}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-secondary small mb-1">First Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={profile.name}
                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                        required
                        style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-secondary small mb-1">Surname</Form.Label>
                      <Form.Control
                        type="text"
                        value={profile.surname}
                        onChange={(e) => setProfile({ ...profile, surname: e.target.value })}
                        required
                        style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-secondary small mb-1">Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        required
                        style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {isOrganization ? (
                      <Form.Group>
                        <Form.Label className="fw-semibold text-secondary small mb-1">Organisation Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={profile.organizationName}
                          onChange={(e) => setProfile({ ...profile, organizationName: e.target.value })}
                          required
                          style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                        />
                      </Form.Group>
                    ) : (
                      <Form.Group>
                        <Form.Label className="fw-semibold text-secondary small mb-1">Student / ID Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={profile.studentNumber || "N/A"}
                          disabled
                          style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px", backgroundColor: "#f3f4f6" }}
                        />
                      </Form.Group>
                    )}
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-secondary small mb-1">Registered Academic Program</Form.Label>
                      <Form.Control
                        type="text"
                        value={profile.program}
                        onChange={(e) => setProfile({ ...profile, program: e.target.value })}
                        style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-secondary small mb-1">Biography / About Me</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={profile.bio}
                        onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                        style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end mt-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    style={{ backgroundColor: "#1B3A87", border: "none", borderRadius: "8px", padding: "10px 24px" }}
                    className="d-flex align-items-center gap-2 fw-bold"
                  >
                    <Save size={16} />
                    {loading ? "Saving Changes..." : "Save Profile Details"}
                  </Button>
                </div>
              </Form>
            </Card>

            {/* Change Password Card */}
            <Card style={sectionCardStyle} className="p-4 text-start">
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="p-2.5 rounded-3" style={{ backgroundColor: "rgba(27, 58, 135, 0.08)", color: "#1B3A87" }}>
                  <Lock size={20} />
                </div>
                <h5 className="fw-bold mb-0" style={{ color: "#1B3A87" }}>Security Credentials</h5>
              </div>

              <Form onSubmit={handlePasswordSave}>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-secondary small mb-1">Current Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="••••••••"
                        value={passwords.current}
                        onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                        required
                        style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-secondary small mb-1">New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="••••••••"
                        value={passwords.new}
                        onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                        required
                        style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-secondary small mb-1">Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="••••••••"
                        value={passwords.confirm}
                        onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                        required
                        style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end mt-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    style={{ backgroundColor: "#1B3A87", border: "none", borderRadius: "8px", padding: "10px 24px" }}
                    className="d-flex align-items-center gap-2 fw-bold"
                  >
                    <Shield size={16} />
                    Update Password
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>

          {/* Quick Preview & Notification Settings */}
          <Col lg={4}>
            {/* Quick Preview Card */}
            <Card style={sectionCardStyle} className="p-4 mb-4 text-center">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white shadow-sm mx-auto mb-3"
                style={{ 
                  width: "72px", 
                  height: "72px", 
                  backgroundColor: "#1B3A87",
                  fontSize: "1.75rem",
                }}
              >
                {(profile.name.charAt(0) + profile.surname.charAt(0)).toUpperCase()}
              </div>
              <h5 className="fw-bold text-dark mb-1">{profile.name} {profile.surname}</h5>
              <p className="text-muted small mb-3">{profile.email}</p>
              <div className="d-flex justify-content-center gap-2">
                <Badge bg="primary" style={{ backgroundColor: "#1B3A87 !important", padding: "6px 12px", borderRadius: "6px" }}>
                  {user?.role === "admin"
                    ? "WCCO Admin"
                    : user?.role === "organization"
                      ? "Project Organisation"
                      : "Volunteer Student"}
                </Badge>
                <Badge bg="info" style={{ backgroundColor: "rgba(27, 58, 135, 0.1) !important", color: "#1B3A87", padding: "6px 12px", borderRadius: "6px" }}>
                  Active Status
                </Badge>
              </div>
            </Card>

            {/* Notifications Card */}
            <Card style={sectionCardStyle} className="p-4 text-start">
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="p-2.5 rounded-3" style={{ backgroundColor: "rgba(27, 58, 135, 0.08)", color: "#1B3A87" }}>
                  <Bell size={20} />
                </div>
                <h5 className="fw-bold mb-0" style={{ color: "#1B3A87" }}>System Alerts</h5>
              </div>

              <Form.Group className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                  <Form.Label className="fw-semibold text-dark mb-0 small">Email Hours Notifications</Form.Label>
                  <p className="text-muted small mb-0">Alerts when log status updates.</p>
                </div>
                <Form.Check 
                  type="switch"
                  id="email-updates"
                  checked={notifications.emailUpdates}
                  onChange={() => handleNotificationToggle("emailUpdates")}
                />
              </Form.Group>

              <hr style={{ color: "#E5E7EB" }} />

              <Form.Group className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                  <Form.Label className="fw-semibold text-dark mb-0 small">New Announcements</Form.Label>
                  <p className="text-muted small mb-0">Emails for global WCCO broadcasts.</p>
                </div>
                <Form.Check 
                  type="switch"
                  id="new-announcements"
                  checked={notifications.newAnnouncements}
                  onChange={() => handleNotificationToggle("newAnnouncements")}
                />
              </Form.Group>

              <hr style={{ color: "#E5E7EB" }} />

              <Form.Group className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                  <Form.Label className="fw-semibold text-dark mb-0 small">Weekly Activity Summary</Form.Label>
                  <p className="text-muted small mb-0">A summary digest of hours completed.</p>
                </div>
                <Form.Check 
                  type="switch"
                  id="weekly-digest"
                  checked={notifications.weeklyDigest}
                  onChange={() => handleNotificationToggle("weeklyDigest")}
                />
              </Form.Group>
            </Card>
          </Col>
        </Row>
      </Container>
    </AppShell>
  );
};

export default SettingsPage;
